// ===============================
// src/OngletGardesBrute.jsx
// ===============================
// Interface Responsable – Port Royal 2
// Liste BRUTE des gardes (Février 2026)
//
// ✅ Version MODIFIABLE :
//  - Lecture via Nginx :
//      • GET  /api/v2/liste_gardes_brute/2026-02
//      • GET  /api/v2/medecins-gardes-*
//      • GET  /api/v2/desiderata-*/2026-02
//  - Édition :
//      • PUT  /api/v2/liste_gardes/2026-02   (payload = { "YYYY-MM-DD": "Nom_Prenom"|null })
//
// ⚠️ Ce composant NE DOIT utiliser que des routes relatives /api/v2/...
//    gérées par Nginx en prod et par vite.config.js en dev.
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { identifierBlocs } from "./algo-astreintes-thoraciques.js";
import { getFrenchHolidayName, isFrenchPublicHoliday } from "./frenchHolidays.mjs";
import { confirmDialog, notify } from "./ui/dialogService.js";
import { apiFetch } from "./apiMedecins.js";
import { useAuth } from "./contexts/AuthContext.jsx";
import { canManageGuardList, manualJuniorListPath, juniorListReadSlug } from "./ui/juniorListAccess.js";
import { withResponsibleBridgeContext } from "./responsableBack.js";

function normalizeMedecinForGardeOptions(m, typeGarde) {
  if (typeGarde !== "ORTHOPEDIE") return m;
  const rawNom = String(m?.nom || "").trim();
  const hasStructuredName = rawNom && String(m?.prenom || "").trim();
  if (hasStructuredName) return m;

  const separatorIndex = rawNom.lastIndexOf("_");
  if (separatorIndex <= 0 || separatorIndex === rawNom.length - 1) return m;
  return {
    ...m,
    nom: rawNom.slice(0, separatorIndex).trim(),
    prenom: rawNom.slice(separatorIndex + 1).trim(),
  };
}

export default function OngletGardesBrute() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ✅ Mois piloté par l'URL : /responsable/gardes-brute?mois=2026-03&type=PR1
  const params = new URLSearchParams(window.location.search);
  const typeGarde = params.get("type") || "PR2"; // ✅ Type de garde pour les retours corrects
  const canConstituteList = canManageGuardList(user, typeGarde);
  const juniorReadSlug = juniorListReadSlug(user, typeGarde);
  const isOrthopedieJunior = typeGarde === "ORTHOPEDIE_JUNIOR";
  const isAstreinteType = ["ASTREINTE_THORACIQUE", "ORTHOPEDIE", "ORTHOPEDIE_JUNIOR"].includes(typeGarde);
  const needsDesiderata = !["ORTHOPEDIE", "ORTHOPEDIE_JUNIOR"].includes(typeGarde);
  const lockSecteur = params.get("lockSecteur") === "1";
  const rawBack = params.get("back") || "";
  const withCurrentBack = (path) => {
    let next = path;
    if (lockSecteur && !/[?&]lockSecteur=/.test(next)) {
      const sep = next.includes("?") ? "&" : "?";
      next = `${next}${sep}lockSecteur=1`;
    }
    if (rawBack && !/[?&]back=/.test(next)) {
      const sep = next.includes("?") ? "&" : "?";
      next = `${next}${sep}back=${encodeURIComponent(rawBack)}`;
    }
    return next;
  };
  const gardesHubPath = () => withCurrentBack(`/responsable/gardes?type=${encodeURIComponent(typeGarde)}`);
  // ✅ Mois par défaut dynamique : mois courant au lieu d'un mois hardcodé
  const defaultMois = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const MOIS = params.get("mois") || defaultMois;

  // ✅ Label lisible pour le type d'activité
  const typeLabel = useMemo(() => {
    switch (typeGarde) {
      case "PR1": return "Port Royal 1";
      case "PR2": return "Port Royal 2";
      case "ASTREINTE_THORACIQUE": return "Astreinte Thoracique";
      case "ORTHOPEDIE": return "Astreinte Orthopédie";
      case "ORTHOPEDIE_JUNIOR": return "Astreinte junior Orthopédie";
      case "OPH": return "Ophtalmologie";
      case "INTERNE1": return "Interne Port Royal 1";
      case "INTERNE2": return "Interne Port Royal 2";
      default: return typeGarde;
    }
  }, [typeGarde]);

  const [yStr, mStr] = String(MOIS).split("-");
  const YEAR = parseInt(yStr, 10);
  const MONTH = parseInt(mStr, 10); // 1..12

  // ✅ Label mois dynamique pour les titres (ex: "Mars 2026")
  const moisLabel = new Date(YEAR, MONTH - 1, 1).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  const moisLabelCap =
    moisLabel && moisLabel.length > 0
      ? moisLabel.charAt(0).toUpperCase() + moisLabel.slice(1)
      : "";

  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [medecins, setMedecins] = useState([]);
  const [liste, setListe] = useState({}); // { "YYYY-MM-DD": "Nom_Prenom"|null }
  const [desidByKey, setDesidByKey] = useState({}); // { "Nom_Prenom": { nbGardes, souhaits } }

  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [noDesiderata, setNoDesiderata] = useState(false); // ✅ Aucun désidérata pour ce mois
  const [nbQuadriParMed, setNbQuadriParMed] = useState({}); // ✅ Comptage astreintes sur le quadrimestre

  // ✅ Pour l'astreinte thoracique : déterminer si un jour est dans un "bloc" (weekend/férié/pont)
  // Utilise identifierBlocs pour calculer les blocs étendus (jeudi avant férié vendredi, etc.)
  const joursEnBlocSet = useMemo(() => {
    if (typeGarde !== "ASTREINTE_THORACIQUE") return new Set();
    // Générer tous les jours du quadrimestre (mai-août 2026)
    const tousLesJours = [];
    for (let m = 5; m <= 8; m++) {
      const nbJours = new Date(2026, m, 0).getDate();
      for (let d = 1; d <= nbJours; d++) {
        tousLesJours.push(`2026-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`);
      }
    }
    const { joursEnBloc } = identifierBlocs(tousLesJours);
    return joursEnBloc; // Set de toutes les dates en bloc
  }, [typeGarde]);
  const isJourBloc = useMemo(() => {
    if (typeGarde !== "ASTREINTE_THORACIQUE") return () => false;
    return (ds) => joursEnBlocSet.has(ds);
  }, [typeGarde, joursEnBlocSet]);

  // ✅ Mois du quadrimestre (mai-août 2026)
  const moisQuadri = useMemo(() => {
    if (typeGarde !== "ASTREINTE_THORACIQUE") return [];
    // Le quadrimestre thoracique = mai à août 2026
    return ["2026-05", "2026-06", "2026-07", "2026-08"];
  }, [typeGarde]);

  // ✅ Construire l'URL de l'API de désidératas selon le type de garde
  const desiderataApiUrl = useMemo(() => {
    if (juniorReadSlug) return `/api/v2/desiderata-${juniorReadSlug}/${MOIS}`;
    switch (typeGarde) {
      case "PR1": return `/api/v2/desiderata-pr1/${MOIS}`;
      case "PR2": return `/api/v2/desiderata-pr2/${MOIS}`;
      case "ASTREINTE_THORACIQUE": return `/api/v2/desiderata-astreinte-thoracique/${MOIS}`;
      case "OPH": return `/api/v2/desiderata-oph/${MOIS}`;
      case "INTERNE1": return `/api/v2/desiderata-interne1/${MOIS}`;
      case "INTERNE2": return `/api/v2/desiderata-interne2/${MOIS}`;
      default: return `/api/v2/desiderata-pr2/${MOIS}`;
    }
  }, [typeGarde, MOIS, juniorReadSlug]);

  // 🔎 utilitaire jour de la semaine
  const jourSemaine = (dateStr) => {
    const jours = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
    const d = new Date(dateStr);
    return jours[d.getDay()];
  };

  const isJuniorOrthopedieAllowedDate = (dateStr) => {
    if (!isOrthopedieJunior) return true;
    const d = new Date(`${dateStr}T12:00:00`);
    const dow = d.getDay();
    return dow === 0 || dow === 6 || isFrenchPublicHoliday(dateStr);
  };

  const keyFromMed = (m) => `${m.nom}_${m.prenom}`;
  const labelFromMed = (m) => `${m.nom} ${m.prenom}`;

  const mapKeyToLabel = useMemo(() => {
    const map = {};
    medecins.forEach((m) => {
      map[keyFromMed(m)] = labelFromMed(m);
    });
    return map;
  }, [medecins]);

  // ✅ Map clé → origine (interieur/exterieur) pour coloration thoracique
  const mapKeyToOrigine = useMemo(() => {
    const map = {};
    medecins.forEach((m) => {
      map[keyFromMed(m)] = m.origine || "interieur";
    });
    return map;
  }, [medecins]);

  // Liste d'options pour le select
  const medecinOptions = useMemo(() => {
    const opts = [
      { value: "", label: "Aucun médecin affecté" }, // null
      ...medecins.map((m) => ({
        value: keyFromMed(m),
        label: labelFromMed(m),
      })),
    ];
    return opts;
  }, [medecins]);

  // ✅ Set des valeurs disponibles (pour détecter une affectation "inconnue")
  const medecinOptionValueSet = useMemo(() => {
    const s = new Set();
    medecinOptions.forEach((o) => s.add(o.value));
    return s;
  }, [medecinOptions]);

  // 🔹 Construction rows à partir de liste + desiderata
  const rebuildRows = (listeState, desidState) => {
    const daysInMonth = new Date(YEAR, MONTH, 0).getDate();
    const newRows = [];

    // ✅ Comptage des gardes obtenues (total) par médecin dans la liste BRUTE
    const nbObtenuesParMed = {};
    Object.values(listeState || {}).forEach((affectKey) => {
      if (!affectKey) return;
      nbObtenuesParMed[affectKey] = (nbObtenuesParMed[affectKey] || 0) + 1;
    });

    for (let i = 1; i <= daysInMonth; i++) {
      const date = `${YEAR}-${String(MONTH).padStart(2, "0")}-${String(i).padStart(
        2,
        "0"
      )}`;

      const affectKey = listeState?.[date] ?? null; // "NOM_PRENOM" ou null
      const dInfo = affectKey ? desidState?.[affectKey] : null;

      const souhaitJour =
        dInfo?.souhaits?.[date]?.label || dInfo?.souhaits?.[date]?.code || "—";

      const nbGardesSouhaitees = dInfo?.nbGardes > 0 ? dInfo.nbGardes : "—";

      const nbGardesObtenues = affectKey ? nbObtenuesParMed[affectKey] || 0 : "—";

      newRows.push({
        date,
        jour: jourSemaine(date),
        affectKey, // clé stockée
        souhaitJour,
        nbGardesSouhaitees,
        nbGardesObtenues,
      });
    }

    return newRows;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setSaveMsg(null);
        setRows([]);
        setListe({});
        setDesidByKey({});
        setDirty(false);
        setNoDesiderata(false);

        // ✅ IMPORTANT : page "BRUTE" => on lit la liste brute selon le type
        // ✅ Utiliser la bonne API de désidératas selon le type de garde
        const bruteUrl = typeGarde === "ASTREINTE_THORACIQUE"
          ? `/api/v2/listes-gardes-astreinte-thoracique/brute/${MOIS}`
          : `/api/v2/liste_gardes_brute/${MOIS}?type=${encodeURIComponent(typeGarde)}`;
        // ✅ Pour l'astreinte thoracique, charger les médecins depuis la liste dédiée (avec champ origine)
        const medUrl = juniorReadSlug
          ? `/api/v2/medecins-gardes-${juniorReadSlug}`
          : typeGarde === "ASTREINTE_THORACIQUE"
          ? "/api/v2/medecins-astreintes-thoracique"
          : typeGarde === "PR1"
          ? "/api/v2/medecins-gardes-pr1"
          : typeGarde === "DIGESTIF"
          ? "/api/v2/medecins-gardes-digestif"
          : typeGarde === "ORTHOPEDIE"
          ? "/api/v2/medecins-astreintes-orthopedie"
          : typeGarde === "ORTHOPEDIE_JUNIOR"
          ? "/api/v2/medecins-astreintes-orthopedie-junior"
          : typeGarde === "INTERNE1"
          ? "/api/v2/medecins-gardes-interne1"
          : typeGarde === "INTERNE2"
          ? "/api/v2/medecins-gardes-interne2"
          : typeGarde === "REANIMATION"
          ? "/api/v2/medecins-gardes-reanimation"
          : "/api/v2/medecins-gardes-pr2";
        const [listeRes, medRes, desidRes] = await Promise.all([
          apiFetch(bruteUrl),
          apiFetch(medUrl),
          needsDesiderata
            ? apiFetch(desiderataApiUrl, { credentials: "include" })
            : Promise.resolve({ ok: true, json: async () => [] }),
        ]);

        // 🔹 Liste des gardes (BRUTE)
        let listeJson = {};
        if (listeRes.ok) {
          listeJson = (await listeRes.json()) || {};
        } else if (listeRes.status !== 404) {
          throw new Error(
            `HTTP ${listeRes.status} lors du chargement de la liste BRUTE des gardes`
          );
        }

        // 🔹 Médecins
        const medJsonRaw = medRes.ok ? (await medRes.json()) || [] : [];
        const medJson = Array.isArray(medJsonRaw)
          ? medJsonRaw.map((m) => normalizeMedecinForGardeOptions(m, typeGarde))
          : [];

        // 🔹 Désiderata
        let desiderata = [];
        if (desidRes.ok) {
          desiderata = (await desidRes.json()) || [];
        }

        // Map nom_prenom → infos desiderata
        const desMap = {};
        let hasAnyDesiderata = false;
        for (const item of desiderata) {
          if (!item || !item.nom || !item.prenom) continue;
          const key = `${item.nom}_${item.prenom}`;
          const souhaits = item.valeurs || {};
          const nbGardes = typeof item.nbGardes === "number"
            ? item.nbGardes
            : parseInt(item.nbGardes, 10) || 0;
          
          // ✅ Vérifier si ce médecin a des désidératas réels (souhaits ou nbGardes > 0)
          if (Object.keys(souhaits).length > 0 || nbGardes > 0) {
            hasAnyDesiderata = true;
          }
          
          desMap[key] = { nbGardes, souhaits };
        }

        // ✅ Marquer si aucun désidérata n'a été formulé pour ce mois
        setNoDesiderata(needsDesiderata && !hasAnyDesiderata);

        setMedecins(medJson);
        setListe(listeJson);
        setDesidByKey(desMap);

        const built = rebuildRows(listeJson, desMap);
        setRows(built);

        // ✅ Charger le comptage quadrimestre pour l'astreinte thoracique
        if (typeGarde === "ASTREINTE_THORACIQUE" && moisQuadri.length > 0) {
          try {
            const quadriCounts = {};
            for (const mc of moisQuadri) {
              const url = `/api/v2/listes-gardes-astreinte-thoracique/brute/${mc}`;
              const res = await apiFetch(url);
              if (res.ok) {
                const data = await res.json();
                for (const [, medK] of Object.entries(data || {})) {
                  if (medK) quadriCounts[medK] = (quadriCounts[medK] || 0) + 1;
                }
              }
            }
            setNbQuadriParMed(quadriCounts);
          } catch (_e) {
            console.log("ℹ️ Impossible de charger les données quadrimestre");
          }
        }

        setLoading(false);
      } catch (e) {
        console.error("❌ Erreur chargement liste brute des gardes :", e);
        setError(e.message || "Erreur inconnue");
        setLoading(false);
        notify("Erreur lors du chargement de la liste des gardes.");
      }
    };

    load();
  }, [MOIS, desiderataApiUrl, needsDesiderata, typeGarde]); // ✅ IMPORTANT : reload quand on change de mois ou de type

  const onChangeAffectation = async (date, newKeyOrEmpty) => {
    const newKey = newKeyOrEmpty ? newKeyOrEmpty : null;

    // ✅ Vérifier si le médecin est indisponible sur ce jour (desiderata)
    // Le code 0 = indisponible, le label contient "Indisponible"
    if (newKey && desidByKey[newKey]) {
      const souhait = desidByKey[newKey].souhaits?.[date];
      const isIndispo = souhait && (
        souhait.code === 0 ||
        String(souhait.code).toLowerCase() === "indisponible" ||
        String(souhait.label || "").toLowerCase().includes("indisponible")
      );
      if (isIndispo) {
        const medLabel = mapKeyToLabel[newKey] || newKey;
        const dateLabel = new Date(date + "T00:00:00").toLocaleDateString("fr-FR");
        const ok = (await confirmDialog(
          `⚠️ ${medLabel} a indiqué "indisponible" pour le ${dateLabel} dans ses desiderata.\n\nVoulez-vous quand même l'affecter sur ce jour ?`
        ));
        if (!ok) return; // Annulation
      }
    }

    setSaveMsg(null);
    setDirty(true);

    // ✅ Calculer l'ancienne clé avant la mise à jour
    const oldKey = (liste || {})[date] || null;

    // ✅ Mettre à jour la liste
    const nextListe = { ...(liste || {}), [date]: newKey };
    setListe(nextListe);

    // ✅ Recalcul rows (dont nb obtenues mois) avec le nouveau state
    setRows(rebuildRows(nextListe, desidByKey));

    // ✅ Mise à jour locale du compteur quadrimestre
    if (typeGarde === "ASTREINTE_THORACIQUE") {
      setNbQuadriParMed((prev) => {
        const updated = { ...prev };
        if (oldKey) updated[oldKey] = Math.max(0, (updated[oldKey] || 0) - 1);
        if (newKey) updated[newKey] = (updated[newKey] || 0) + 1;
        return updated;
      });
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      setSaveMsg(null);

      // On envoie uniquement le mapping date -> key|null
      const payload = { ...(liste || {}) };

      // ✅ Utiliser la route backend existante : POST /api/v2/liste_gardes/enregistrer_brute
      // ✅ On passe le type pour que le backend enregistre dans le bon fichier
      const res = await apiFetch("/api/v2/liste_gardes/enregistrer_brute", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mois: MOIS, liste: payload, type: typeGarde }),
      });

      if (!res.ok) {
        // Améliorer la gestion d'erreur : détecter si la réponse est JSON ou HTML
        const contentType = res.headers.get("content-type");
        let errorMessage = `HTTP ${res.status}`;
        
        if (contentType && contentType.includes("application/json")) {
          try {
            const json = await res.json();
            errorMessage = json.error || json.message || errorMessage;
          } catch {
            // Si le JSON est invalide, on garde le message par défaut
          }
        } else {
          // Réponse non-JSON (probablement HTML d'erreur)
          const txt = await res.text().catch(() => "");
          errorMessage = `HTTP ${res.status} - ${res.url}\n${txt.substring(0, 200)}`;
        }
        
        throw new Error(`Erreur sauvegarde liste brute : ${errorMessage}`);
      }

      // ✅ Après save, on refresh les rows à partir de l'état local (liste)
      setRows(rebuildRows(payload, desidByKey));

      setDirty(false);
      setSaveMsg("✅ Modifications enregistrées.");
    } catch (e) {
      console.error("❌ Erreur sauvegarde liste brute :", e);
      notify(
        `Erreur lors de l'enregistrement de la liste brute : ${
          e.message || "Erreur inconnue"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  // ✅ NOUVEAU : Valider la liste (crée la VALIDÉE à partir de la BRUTE côté serveur)
  const validateListe = async () => {
    try {
      setSaveMsg(null);

      if (dirty) {
        const proceed = (await confirmDialog(
          "⚠️ Il y a des modifications non enregistrées.\n\nVoulez-vous continuer la validation quand même ?\n(Sinon, cliquez Annuler puis enregistrez d’abord.)"
        ));
        if (!proceed) return;
      }

      // ✅ IMPORTANT : on envoie aussi la liste courante (évite toute ambiguïté de mois/état)
      const payload = { ...(liste || {}) };

      const { guardedDutyFetch } = await import('./ui/guardWarnings.js');
      const res = await guardedDutyFetch("/api/v2/liste_gardes/valider", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mois: MOIS, liste: payload, type: typeGarde }),
      });

      if (!res) return;
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} validation. ${txt}`);
      }

      // Une fois validée, on va sur la page validée (même mois, même type)
      navigate(withCurrentBack(`/responsable/gardes-valide?mois=${encodeURIComponent(MOIS)}&type=${encodeURIComponent(typeGarde)}`));
    } catch (e) {
      console.error("❌ Erreur validation liste :", e);
      notify(
        `Erreur lors de la validation de la liste : ${e.message || "Erreur inconnue"}`
      );
    }
  };

  // ===============================
  // Styles
  // ===============================

  const containerStyle = {
    maxWidth: "1100px",
    margin: "40px auto",
    padding: "24px",
    fontFamily: "Inter, sans-serif",
  };

  const blackBtn = (disabled = false) => ({
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid #111827",
    background: disabled ? "#6b7280" : "#111827",
    color: "#ffffff",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 700,
    opacity: disabled ? 0.85 : 1,
  });

  const th = {
    padding: "8px",
    border: "1px solid #e5e7eb",
    background: "#f3f4f6",
    fontWeight: 700,
    fontSize: 14,
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const td = {
    padding: "8px",
    border: "1px solid #f1f5f9",
    fontSize: 14,
    verticalAlign: "top",
  };

  const selectStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    fontSize: 14,
  };

  // ===============================
  // Rendu
  // ===============================

  if (!canConstituteList) {
    const manualPath = manualJuniorListPath(user, typeGarde, MOIS);
    return <div style={containerStyle}>
      <button onClick={() => window.history.back()}>Retour</button>
      {manualPath && <button onClick={() => window.location.assign(withResponsibleBridgeContext(manualPath, { backMode: 'current' }))}>Modifier la liste manuellement</button>}
    </div>;
  }
  if (loading) {
    return (
      <div style={containerStyle}>
        <button style={blackBtn(false)} onClick={() => navigate(gardesHubPath())}>
          ← {typeGarde === "ASTREINTE_THORACIQUE" ? "Retour à la gestion des astreintes" : "Retour à la gestion des gardes"}
        </button>
        <p style={{ marginTop: 14 }}>Chargement de la liste brute des gardes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <button style={blackBtn(false)} onClick={() => navigate(gardesHubPath())}>
          ← {typeGarde === "ASTREINTE_THORACIQUE" ? "Retour à la gestion des astreintes" : "Retour à la gestion des gardes"}
        </button>

        <h1 style={{ fontSize: 28, fontWeight: 800, margin: "16px 0 12px" }}>
          Liste brute des gardes ({moisLabelCap})
        </h1>
        <p style={{ color: "#b91c1c" }}>
          ❌ Erreur lors du chargement de la liste : {error}
        </p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
        <button style={blackBtn(false)} onClick={() => navigate(gardesHubPath())}>
          ← {typeGarde === "ASTREINTE_THORACIQUE" ? "Retour à la gestion des astreintes" : "Retour à la gestion des gardes"}
        </button>
        <LogoutButton />
      </div>

      <h1 style={{ fontSize: 28, fontWeight: 800, margin: "16px 0 8px" }}>
        Liste brute {typeLabel} ({moisLabelCap})
      </h1>

      {/* Sélecteur de mois du quadrimestre pour l'astreinte thoracique */}
      {typeGarde === "ASTREINTE_THORACIQUE" && moisQuadri.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: "#1e40af" }}>Quadrimestre :</span>
          {moisQuadri.map((mc) => {
            const isActive = mc === MOIS;
            const mlabel = new Date(parseInt(mc.split("-")[0]), parseInt(mc.split("-")[1]) - 1, 1)
              .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
            const mlabelCap = mlabel.charAt(0).toUpperCase() + mlabel.slice(1);
            return (
              <button
                key={mc}
                type="button"
                onClick={() => {
                  if (!isActive) navigate(withCurrentBack(`/responsable/gardes-brute?mois=${encodeURIComponent(mc)}&type=${encodeURIComponent(typeGarde)}`));
                }}
                style={{
                  padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: isActive ? 700 : 400,
                  border: isActive ? "2px solid #3b82f6" : "1px solid #d1d5db",
                  background: isActive ? "#dbeafe" : "#fff",
                  color: isActive ? "#1e40af" : "#374151",
                  cursor: isActive ? "default" : "pointer",
                }}
              >
                {mlabelCap}
              </button>
            );
          })}
          <span style={{ marginLeft: 8, fontSize: 12, color: "#374151", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <span style={{ display: "inline-block", width: 14, height: 14, background: "#f0fdf4", border: "2px solid #86efac", borderRadius: 3 }}></span>
              <span style={{ fontWeight: 600, color: "#166534" }}>Intérieur</span>
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <span style={{ display: "inline-block", width: 14, height: 14, background: "#eff6ff", border: "2px solid #93c5fd", borderRadius: 3 }}></span>
              <span style={{ fontWeight: 600, color: "#1e40af" }}>Extérieur</span>
            </span>
          </span>
        </div>
      )}

      <p style={{ color: "#374151", marginBottom: 12 }}>
        Tu peux modifier manuellement l’affectation de chaque jour (sélection du médecin),
        puis enregistrer. Les desiderata servent ici uniquement d’aide à la lecture.
      </p>

      {/* ✅ Avertissement si aucun désidérata n'a été formulé pour ce mois */}
      {noDesiderata && (
        <div style={{
          padding: "16px 20px",
          marginBottom: 16,
          borderRadius: 12,
          border: "1px solid #f59e0b",
          background: "#fffbeb",
          color: "#92400e",
        }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
            ⚠️ Aucun désidérata n'a été formulé pour {typeLabel} ({moisLabelCap})
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 14 }}>
            Les médecins n'ont pas encore saisi leurs souhaits pour ce mois. 
            Vous pouvez tout de même créer une liste manuellement, mais il est recommandé d'attendre 
            que les désidératas soient recueillis.
          </p>
        </div>
      )}

      {saveMsg ? (
        <p style={{ margin: "8px 0 14px", color: "#065f46", fontWeight: 700 }}>
          {saveMsg}
        </p>
      ) : null}

      <div
        style={{
          overflowX: "auto",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          background: "#ffffff",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
          <thead>
            <tr>
              <th style={th}>Jour</th>
              <th style={th}>Date</th>
              <th style={th}>{isAstreinteType ? "Médecin d'astreinte (modifiable)" : "Médecin de garde (modifiable)"}</th>
              <th style={th}>Souhait initial sur ce jour</th>
              {!isAstreinteType && <th style={th}>Gardes souhaitées (total)</th>}
              <th style={th}>{isAstreinteType ? "Astreintes (mois)" : "Gardes obtenues (total)"}</th>
              {typeGarde === "ASTREINTE_THORACIQUE" && <th style={th}>Astreintes (quadrimestre)</th>}
            </tr>
          </thead>

          <tbody>
            {rows.map((r, idx) => {
              const hasMed = !!r.affectKey;
              const isKnown = !r.affectKey ? true : medecinOptionValueSet.has(r.affectKey);
              const currentValue = r.affectKey || "";

              const optionsForRow =
                r.affectKey && !isKnown
                  ? [
                      { value: r.affectKey, label: `⚠️ Médecin inconnu : ${r.affectKey}` },
                      ...medecinOptions,
                    ]
                  : medecinOptions;

              const isBlocJour = isJourBloc(r.date);
              const blocBg = isBlocJour ? "#fff7ed" : undefined; // fond orange clair pour les blocs
              const juniorOrthoAllowed = isJuniorOrthopedieAllowedDate(r.date);
              const rowDisabled = isOrthopedieJunior && !juniorOrthoAllowed;
              const holidayName = getFrenchHolidayName(r.date);

              return (
                <tr key={r.date + idx} style={rowDisabled ? { background: "#f9fafb", opacity: 0.72 } : isBlocJour ? { background: blocBg } : undefined}>
                  <td style={{ ...td, ...(isBlocJour ? { fontWeight: 700, color: "#9a3412" } : {}) }}>
                    {r.jour}
                    {isOrthopedieJunior && juniorOrthoAllowed && holidayName ? (
                      <div style={{ fontSize: 11, color: "#9a3412", fontWeight: 700 }}>{holidayName}</div>
                    ) : null}
                  </td>

                  <td style={{ ...td, ...(isBlocJour ? { fontWeight: 600, color: "#9a3412" } : {}) }}>{new Date(r.date).toLocaleDateString("fr-FR")}</td>

                  {(() => {
                    const origMed = r.affectKey ? mapKeyToOrigine[r.affectKey] : null;
                    const isInt = origMed === "interieur";
                    const isExt = origMed === "exterieur";
                    const medBg = typeGarde === "ASTREINTE_THORACIQUE" && r.affectKey
                      ? (isInt ? "#f0fdf4" : isExt ? "#eff6ff" : undefined)
                      : undefined;
                    const medBorder = typeGarde === "ASTREINTE_THORACIQUE" && r.affectKey
                      ? (isInt ? "#86efac" : isExt ? "#93c5fd" : "#d1d5db")
                      : (hasMed ? "#d1d5db" : "#ef4444");
                    return (
                  <td style={{ ...td, background: medBg }}>
                    <select
                      style={{
                        ...selectStyle,
                        borderColor: medBorder,
                        background: medBg || "#fff",
                      }}
                      value={currentValue}
                      disabled={rowDisabled}
                      onChange={(e) => onChangeAffectation(r.date, e.target.value)}
                    >
                      {optionsForRow.map((opt) => (
                        <option key={opt.value || "none"} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    {rowDisabled ? (
                      <div style={{ marginTop: 6, color: "#6b7280", fontWeight: 700 }}>
                        Non saisissable (hors samedi/dimanche/férié)
                      </div>
                    ) : !hasMed ? (
                      <div style={{ marginTop: 6, color: "#b91c1c", fontWeight: 700 }}>
                        Aucun médecin affecté
                      </div>
                    ) : (
                      <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                        {!isAstreinteType && (
                          <>
                            Clé : {r.affectKey}{" "}
                            {mapKeyToLabel[r.affectKey] ? `(${mapKeyToLabel[r.affectKey]})` : ""}
                          </>
                        )}
                        {!isKnown ? (
                          <span style={{ marginLeft: 8, color: "#b45309", fontWeight: 800 }}>
                            ⚠️ Inconnu dans la liste des médecins
                          </span>
                        ) : null}
                      </div>
                    )}
                  </td>
                    );
                  })()}

                  <td style={td}>{r.souhaitJour}</td>
                  {!isAstreinteType && <td style={td}>{r.nbGardesSouhaitees}</td>}
                  <td style={td}>{r.nbGardesObtenues}</td>
                  {typeGarde === "ASTREINTE_THORACIQUE" && (
                    <td style={{ ...td, fontWeight: 600 }}>
                      {r.affectKey ? (nbQuadriParMed[r.affectKey] || 0) : "—"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: 18,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div style={{ color: "#374151", fontSize: 13 }}>
          {dirty ? (
            <span style={{ fontWeight: 800, color: "#92400e" }}>
              ⚠️ Modifications non enregistrées
            </span>
          ) : (
            <span style={{ fontWeight: 700, color: "#065f46" }}>
              ✔️ Aucune modification en attente
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            style={blackBtn(!dirty || saving)}
            disabled={!dirty || saving}
            onClick={save}
            title={!dirty ? "Aucune modification à enregistrer" : "Enregistrer"}
          >
            {saving ? "Enregistrement..." : "Enregistrer les modifications"}
          </button>

          <button style={blackBtn(false)} onClick={() => navigate(gardesHubPath())}>
            {typeGarde === "ASTREINTE_THORACIQUE" ? "Retour à la gestion des astreintes" : "Retour à la gestion des gardes"}
          </button>

          {/* ✅ Export PDF liste brute */}
          <button
            style={blackBtn(false)}
            onClick={() => window.open(`/api/v2/liste_gardes_brute/${MOIS}/pdf?type=${encodeURIComponent(typeGarde)}`, "_blank")}
          >
            Télécharger PDF (liste brute)
          </button>

          {/* ✅ Export Excel liste brute avec menus déroulants et formules COUNTIF */}
          <button
            style={blackBtn(false)}
            onClick={async () => {
              try {
                const ExcelJS = (await import("exceljs")).default;
                const { saveAs } = await import("file-saver");

                const isTho = typeGarde === "ASTREINTE_THORACIQUE";
                const wb = new ExcelJS.Workbook();
                wb.creator = "Planning Anesthésie";
                wb.created = new Date();

                // ── Feuille cachée de référence médecins (pour menus déroulants) ──
                const medNames = medecins.map((m) => labelFromMed(m)).sort((a, b) => a.localeCompare(b, "fr"));
                const refSheet = wb.addWorksheet("_Ref_Medecins");
                refSheet.state = "veryHidden";
                medNames.forEach((name, i) => { refSheet.getCell(`A${i + 1}`).value = name; });

                // ── Map clé → label pour convertir les affectations ──
                const keyToLabelLocal = {};
                medecins.forEach((m) => { keyToLabelLocal[keyFromMed(m)] = labelFromMed(m); });
                const keyToOrigineLocal = {};
                medecins.forEach((m) => { keyToOrigineLocal[keyFromMed(m)] = m.origine || "interieur"; });

                // ── Charger les données de TOUS les mois du quadrimestre ──
                const allMonths = isTho && moisQuadri.length > 0 ? moisQuadri : [MOIS];
                const monthsData = {};
                for (const mc of allMonths) {
                  const bruteUrl = isTho
                    ? `/api/v2/listes-gardes-astreinte-thoracique/brute/${mc}`
                    : `/api/v2/liste_gardes_brute/${mc}?type=${encodeURIComponent(typeGarde)}`;
                  try {
                    const res = await apiFetch(bruteUrl);
                    monthsData[mc] = res.ok ? (await res.json()) || {} : {};
                  } catch (_) { monthsData[mc] = {}; }
                }

                // ── Noms d'onglets (ex: "Mai 2026") ──
                const sheetNames = {};
                allMonths.forEach((mc) => {
                  const [y, m] = mc.split("-");
                  const lbl = new Date(parseInt(y), parseInt(m) - 1, 1)
                    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
                  sheetNames[mc] = lbl.charAt(0).toUpperCase() + lbl.slice(1);
                });

                // Colonne médecin = C (index 3), astreintes mois = F (6), quadrimestre = G (7) pour tho
                const medCol = "C";   // 3e colonne (Jour=A, Date=B, Médecin=C)
                const moisCol = isTho ? "F" : "E"; // selon les colonnes
                const quadriCol = "G";

                // ── Créer un onglet par mois ──
                const sheetInfos = {}; // mc → { sheetName, dataRowCount }
                for (const mc of allMonths) {
                  const listeForMonth = monthsData[mc];
                  const [yr, mo] = mc.split("-").map(Number);
                  const nbDays = new Date(yr, mo, 0).getDate();
                  const sName = sheetNames[mc];
                  const ws = wb.addWorksheet(sName);

                  // ── Colonnes ──
                  const cols = [
                    { header: "Jour", key: "jour", width: 8 },
                    { header: "Date", key: "date", width: 14 },
                    { header: isTho ? "Médecin d'astreinte" : "Médecin de garde", key: "medecin", width: 30 },
                  ];
                  if (isTho) cols.push({ header: "Origine", key: "origine", width: 12 });
                  cols.push({ header: "Souhait initial", key: "souhait", width: 18 });
                  if (!isTho) cols.push({ header: "Gardes souhaitées", key: "nbSouhaitees", width: 16 });
                  cols.push({ header: isTho ? "Astreintes (mois)" : "Gardes obtenues", key: "nbMois", width: 18 });
                  if (isTho) cols.push({ header: "Astreintes (quadrimestre)", key: "nbQuadri", width: 24 });
                  ws.columns = cols;

                  // ── Style en-tête ──
                  const headerRow = ws.getRow(1);
                  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
                  headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1e3a5f" } };
                  headerRow.alignment = { vertical: "middle", horizontal: "center" };
                  headerRow.height = 22;

                  // ── Désidératas pour ce mois (si c'est le mois courant, utiliser desidByKey, sinon pas de souhait) ──
                  const desidForMonth = mc === MOIS ? desidByKey : {};

                  // ── Remplir les lignes ──
                  const jours = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
                  for (let d = 1; d <= nbDays; d++) {
                    const dateStr = `${yr}-${String(mo).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                    const jourLabel = jours[new Date(dateStr).getDay()];
                    const affKey = listeForMonth[dateStr] || null;
                    const medLabel = affKey ? (keyToLabelLocal[affKey] || affKey) : "";
                    const origMed = affKey ? (keyToOrigineLocal[affKey] || "") : "";
                    const origLabel = origMed === "interieur" ? "Intérieur" : origMed === "exterieur" ? "Extérieur" : "";
                    const dateFormatted = new Date(dateStr).toLocaleDateString("fr-FR");
                    const souhait = affKey && desidForMonth[affKey]?.souhaits?.[dateStr]
                      ? (desidForMonth[affKey].souhaits[dateStr].label || desidForMonth[affKey].souhaits[dateStr].code || "—") : "—";

                    const rowData = { jour: jourLabel, date: dateFormatted, medecin: medLabel };
                    if (isTho) rowData.origine = origLabel;
                    rowData.souhait = souhait;
                    if (!isTho) rowData.nbSouhaitees = "—";
                    rowData.nbMois = 0;   // placeholder, sera remplacé par formule
                    if (isTho) rowData.nbQuadri = 0; // placeholder

                    const exRow = ws.addRow(rowData);
                    const rowNum = exRow.number;

                    // ── Coloration fond selon origine ──
                    if (isTho && affKey) {
                      const isInt = origMed === "interieur";
                      const isExt = origMed === "exterieur";
                      const medCell = exRow.getCell("medecin");
                      if (isInt) {
                        medCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFf0fdf4" } };
                        medCell.font = { color: { argb: "FF166534" } };
                      } else if (isExt) {
                        medCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFeff6ff" } };
                        medCell.font = { color: { argb: "FF1e40af" } };
                      }
                      const origCell = exRow.getCell("origine");
                      if (isInt) {
                        origCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFdcfce7" } };
                        origCell.font = { bold: true, color: { argb: "FF166534" } };
                      } else if (isExt) {
                        origCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFdbeafe" } };
                        origCell.font = { bold: true, color: { argb: "FF1e40af" } };
                      }
                    }

                    // ── Fond orange pour les jours en bloc ──
                    if (isTho && isJourBloc(dateStr)) {
                      ["jour", "date"].forEach((k) => {
                        const c = exRow.getCell(k);
                        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFfff7ed" } };
                        c.font = { bold: true, color: { argb: "FF9a3412" } };
                      });
                    }

                    // ── Formule COUNTIF pour Astreintes (mois) ── 
                    // Compte combien de fois ce nom de médecin apparaît dans la colonne C de cette feuille
                    const moisCellRef = isTho ? moisCol : moisCol;
                    const lastDataRow = nbDays + 1; // +1 car header est en ligne 1
                    ws.getCell(`${moisCellRef}${rowNum}`).value = {
                      formula: `IF(${medCol}${rowNum}="","",COUNTIF(${medCol}$2:${medCol}$${lastDataRow},${medCol}${rowNum}))`,
                    };

                    // ── Formule COUNTIF inter-feuilles pour Astreintes (quadrimestre) ──
                    if (isTho) {
                      // SUM de COUNTIF sur chaque feuille du quadrimestre
                      const parts = allMonths.map((omc) => {
                        const oName = sheetNames[omc];
                        const [oy, om] = omc.split("-").map(Number);
                        const oNbDays = new Date(oy, om, 0).getDate();
                        const oLastRow = oNbDays + 1;
                        return `COUNTIF('${oName}'!${medCol}$2:${medCol}$${oLastRow},${medCol}${rowNum})`;
                      });
                      ws.getCell(`${quadriCol}${rowNum}`).value = {
                        formula: `IF(${medCol}${rowNum}="","",${parts.join("+")})`,
                      };
                    }

                    // Bordures fines
                    exRow.eachCell((cell) => {
                      cell.border = {
                        top: { style: "thin", color: { argb: "FFe5e7eb" } },
                        bottom: { style: "thin", color: { argb: "FFe5e7eb" } },
                        left: { style: "thin", color: { argb: "FFe5e7eb" } },
                        right: { style: "thin", color: { argb: "FFe5e7eb" } },
                      };
                      cell.alignment = { vertical: "middle" };
                    });
                  }

                  // ── Data validation (menus déroulants) sur colonne Médecin ──
                  for (let i = 2; i <= nbDays + 1; i++) {
                    ws.getCell(`${medCol}${i}`).dataValidation = {
                      type: "list",
                      allowBlank: true,
                      formulae: [`_Ref_Medecins!$A$1:$A$${medNames.length}`],
                      showErrorMessage: true,
                      errorTitle: "Valeur invalide",
                      error: "Veuillez sélectionner un médecin dans la liste.",
                    };
                  }

                  sheetInfos[mc] = { sheetName: sName, dataRowCount: nbDays };
                }

                // ── Générer et télécharger ──
                const buffer = await wb.xlsx.writeBuffer();
                const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                // Numéro du quadrimestre : Q1=jan-avr, Q2=mai-août, Q3=sept-déc
                // Pour mai-août 2026 → quadrimestre 2 (ou 3 selon convention métier : 1=sept-déc, 2=jan-avr, 3=mai-août)
                const firstMonth = allMonths[0]; // ex: "2026-05"
                const fm = parseInt(firstMonth.split("-")[1], 10);
                const quadriNum = fm <= 4 ? 1 : fm <= 8 ? 2 : 3;
                // Convention métier : mai-août = quadrimestre 3
                const quadriLabel = fm >= 5 && fm <= 8 ? 3 : fm >= 9 ? 1 : 2;
                const fname = isTho
                  ? `liste_brute_astreinte_thoracique_quadrimestre_${quadriLabel}.xlsx`
                  : `liste_brute_${typeGarde}_${MOIS}.xlsx`;
                saveAs(blob, fname);
              } catch (err) {
                console.error("Erreur export Excel:", err);
                notify("Erreur lors de la génération du fichier Excel.");
              }
            }}
          >
            Télécharger Excel (liste brute)
          </button>

          <button style={blackBtn(false)} onClick={validateListe}>
            {typeGarde === "ASTREINTE_THORACIQUE" ? "Valider la liste d'astreintes" : "Valider la liste de garde"}
          </button>
        </div>
      </div>
    </div>
  );
}
